//TODO: Email and Instagram icons should link to the coach's social media profiles

import React, { useState } from 'react';
import {
  Card, CardMedia, CardContent, Typography, Box
} from '@mui/material';
import InstagramIcon from '@mui/icons-material/Instagram';
import { motion } from 'framer-motion';

/*
 * CoachCard
 * ---------
 * Card component showing a coach's photo, name, classes, and a short description.
 * Truncation with a "Read more" togglefor long descriptions. 
 * Social links (Instagram/email) are rendered when provided by the 'coach' prop.
 *
 * Props:
 * - coach: object { first_name, last_name, photo, description, classes, instagram, email }
 * - delay: number (optional) to stagger the framer-motion entry animation.
 */


import config from '../config'; 
const url = config.API_URL;

function CoachCard({ coach, delay }) {
  const [expanded, setExpanded] = useState(false);
  const charLimit = 200;
  const isLong = coach.description.length > charLimit;
  const preview = coach.description.slice(0, charLimit);
  let photoUrl = coach.photo
    ? (typeof coach.photo === 'string' && coach.photo.startsWith('http')
        ? coach.photo
        : url + coach.photo)
    : '';
  
  // Optimize coach photos from Cloudinary
  if (photoUrl.includes('cloudinary.com')) {
    photoUrl = photoUrl.replace('/upload/', '/upload/f_auto,q_auto,w_400,c_limit/');
  }
  
  const altText = coach.alt_text || `${coach.first_name} ${coach.last_name}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.4, delay }}
      viewport={{ once: true, margin: "-50px" }}
      style={{ height: '100%', willChange: 'opacity, transform' }}
    >
      <Card
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          backgroundColor: '#222',
          color: '#fff',
          borderRadius: 2,
          boxShadow: 4,
          transition: 'transform 0.3s ease-in-out',
          p: { xs: 2, sm: 2 }
        }}
      >
        <CardMedia
          component="img"
          image={photoUrl}
          alt={altText}
          loading="lazy"
          width="400"
          height="600"
          sx={{ 
            height: { xs: 450, sm: 400 }, 
            // width: { xs: 'auto', sm: 'auto' },
            borderRadius: 1, 
            objectFit: 'cover',
            objectPosition: { xs: 'center 20%', sm: 'center 15%' }
          }}
        />
        <CardContent
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            px: { xs: 0, sm: 1 }
          }}
        >
          <Box>
            <Typography variant="h6" color="secondary">{coach.first_name + ' ' + coach.last_name}</Typography>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {coach.classes.join(', ')}
            </Typography>
            <Typography variant="body2" sx={{ color: '#ccc' }}>
              {isLong && !expanded ? `${preview}...` : coach.description}
            </Typography>
            {isLong && (
              <Typography
                variant="caption"
                sx={{ color: 'gray', mt: 1, cursor: 'pointer', fontStyle: 'italic' }}
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? 'Show less' : 'Read more'}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            {coach.instagram && (
              <a
                href={coach.instagram}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'inherit', display: 'inline-flex' }}
                aria-label={`Open ${coach.first_name} ${coach.last_name}'s Instagram`}
              >
                <InstagramIcon sx={{ color: '#bbb', cursor: 'pointer', '&:hover': { color: '#fff' } }} />
              </a>
            )}
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );
}

export default CoachCard;
